// FunZonePrinter.js
// Same pattern as PrinterCommonfile.js — single printer from FunZoneSettings

import io from "socket.io-client";
import { get_funzone_settings, server_post_data } from "./serviceconnection";

// ─── Socket Setup ────────────────────────────────────────────────────────────
let socket = null;

const initPrinterSocket = () => {
  if (socket) return;
  socket = io("http://localhost:3998", {
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 2000,
  });
  socket.on("connect_error", (err) =>
    console.warn("⚠️ Printer helper error:", err.message),
  );
};

initPrinterSocket();

// ─── Printer Config Cache ────────────────────────────────────────────────────
let cachedPrinterConfig = null; // loaded from API

// Call once at app startup (or before first print)
export const loadFunZonePrinterSettings = async () => {
  try {
    const fd = new FormData();
    const response = await server_post_data(get_funzone_settings, fd);

    console.log("🖨️ Printer settings raw response:", response.data); // dekho kya aa raha hai

    if (response.data.status === 1) {
      cachedPrinterConfig = response.data.data; // ← use the inner .data object
      console.log("✅ Printer config cached:", cachedPrinterConfig);
      // Optional extra logs for confirmation
      console.log("  → Type:", cachedPrinterConfig.printer_type);
      console.log("  → IP:  ", cachedPrinterConfig.printer_ip);
      console.log("  → Port:", cachedPrinterConfig.printer_port);
    } else {
      console.warn("⚠️ Settings API returned status != 1:", response.data);
    }
  } catch (err) {
    console.error("❌ Failed to load FunZone printer settings:", err);
  }
};

// ─── Send to Printer Helper ──────────────────────────────────────────────────
// ─── Send to Printer Helper ──────────────────────────────────────────────────
const sendToPrinterHelper = (content) => {
  console.log("sssss", "dddddd");
  if (!socket || !socket.connected) {
    console.error("❌ Printer helper not connected");
    return;
  }
  if (!cachedPrinterConfig) {
    console.error(
      "❌ Printer config not loaded. Call loadFunZonePrinterSettings first.",
    );
    return;
  }
  console.log("sssss", "dddddd 21");

  const printerTypeRaw =
    cachedPrinterConfig.printer_type?.toLowerCase() || "usb";

  let type = "usb"; // default fallback

  if (printerTypeRaw === "lan" || printerTypeRaw === "network") {
    type = "network";
  } else if (printerTypeRaw === "usb") {
    type = "usb";
  } else {
    console.warn(
      `Unsupported printer_type: ${printerTypeRaw} — falling back to USB`,
    );
    type = "usb";
  }

  const payload = {
    type, // will be "network" or "usb"

    content,

    // only sent/used when type === "network"
    ip_address: type === "network" ? cachedPrinterConfig.printer_ip : undefined,
    port:
      type === "network"
        ? Number(cachedPrinterConfig.printer_port) || 9100
        : undefined,

    // only sent/used when type === "usb"
    printer_name: type === "usb" ? cachedPrinterConfig.printer_name : undefined,

    // optional: if your backend supports VID/PID identification
    // usb_vid: cachedPrinterConfig.usb_vid,
    // usb_pid: cachedPrinterConfig.usb_pid,
  };

  console.log("[DEBUG] Sending print payload:", payload);
  socket.emit("print", payload);
};

// ─── Width Profile ────────────────────────────────────────────────────────────
const getWidthProfile = () => {
  const width = Number(cachedPrinterConfig?.printer_width_mm) || 80;
  if (width >= 76) return { chars: 48 };
  if (width >= 70) return { chars: 42 };
  return { chars: 32 };
};

// ─── ESC/POS Helpers ──────────────────────────────────────────────────────────
const enc = (str) => new TextEncoder().encode(str);
const lpad = (val, w) => String(val).padEnd(w).slice(0, w);
const rpad = (val, w) => String(val).padStart(w).slice(-w);
const line = (chars) => "-".repeat(chars) + "\n";

// ─── Format Ticket ────────────────────────────────────────────────────────────
const formatTicket = (ticketData) => {
  const { chars } = getWidthProfile();

  /*
    ticketData shape:
    {
      ticket_number,
      billing_type,          // "NORMAL" | "FAST_TRACK"
      parent_mobile,
      kid_name,
      no_of_kids,
      check_in_time,         // ISO string
      duration_minutes,
      extended_minutes,
      rate_per_hour,
      total_amount,
      payment_mode,          // "CASH" | "UPI"
      transaction_id,
      venue_name,            // from settings or hardcoded
    }
  */

  let cmd = [];

  // Init
  cmd.push(27, 64); // ESC @ reset
  cmd.push(27, 77, 0); // Font A
  cmd.push(27, 97, 1); // Center

  // ── Header ────────────────────────────────────────────────────────────────
  cmd.push(27, 69, 1);
  cmd.push(29, 33, 17); // double width+height
  cmd.push(...enc((ticketData.venue_name || "FunZone") + "\n"));
  cmd.push(29, 33, 0);
  cmd.push(27, 69, 0);

  cmd.push(...enc("Play Zone Ticket\n"));
  cmd.push(...enc(line(chars)));

  // ── Ticket Info ───────────────────────────────────────────────────────────
  cmd.push(27, 97, 0); // Left align

  const checkIn = new Date(ticketData.check_in_time || Date.now());
  const dateStr = checkIn.toLocaleDateString("en-IN");
  const timeStr = checkIn.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  cmd.push(...enc(`Ticket #: ${ticketData.ticket_number || "-"}\n`));
  cmd.push(...enc(`Type    : ${ticketData.billing_type || "NORMAL"}\n`));
  cmd.push(...enc(`Date    : ${dateStr}  ${timeStr}\n`));
  cmd.push(...enc(line(chars)));

  // ── Kid / Parent Details ──────────────────────────────────────────────────
  if (ticketData.kid_name) {
    cmd.push(...enc(`Kid     : ${ticketData.kid_name}\n`));
  }
  cmd.push(...enc(`Kids    : ${ticketData.no_of_kids || 1}\n`));
  cmd.push(...enc(`Mobile  : ${ticketData.parent_mobile || "-"}\n`));
  cmd.push(...enc(line(chars)));

  // ── Time Details ──────────────────────────────────────────────────────────
  const totalMins =
    (ticketData.duration_minutes || 60) + (ticketData.extended_minutes || 0);
  const checkInFmt = checkIn.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  // Compute release time
  const releaseTime = new Date(checkIn.getTime() + totalMins * 60 * 1000);
  const releaseFmt = releaseTime.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  cmd.push(...enc(`Check-in : ${checkInFmt}\n`));
  cmd.push(...enc(`Duration : ${ticketData.duration_minutes} min`));
  if (ticketData.extended_minutes > 0) {
    cmd.push(...enc(` + ${ticketData.extended_minutes} min ext`));
  }
  cmd.push(...enc("\n"));
  cmd.push(...enc(`Release  : ${releaseFmt}\n`));
  cmd.push(...enc(line(chars)));

  // ── Amount ────────────────────────────────────────────────────────────────
  const rate = Number(ticketData.rate_per_hour) || 0;
  const total = Number(ticketData.total_amount) || 0;

  cmd.push(...enc(`Rate     : Rs. ${rate.toFixed(2)}/hr\n`));
  cmd.push(...enc(`Kids     : x${ticketData.no_of_kids || 1}\n`));
  cmd.push(...enc(line(chars)));

  // Grand total — bold double width
  const grandLabel = "Total";
  const grandAmt = "Rs. " + total.toFixed(2);
  const eff = Math.floor(chars / 2);
  const totalLine = lpad(grandLabel, eff - grandAmt.length) + grandAmt;

  cmd.push(27, 69, 1);
  cmd.push(29, 33, 16); // double width only
  if (totalLine.length <= eff) {
    cmd.push(...enc(totalLine + "\n"));
  } else {
    cmd.push(...enc(`${grandLabel}: ${grandAmt}\n`));
  }
  cmd.push(29, 33, 0);
  cmd.push(27, 69, 0);

  // Payment mode
  cmd.push(...enc(`Payment  : ${ticketData.payment_mode || "CASH"}\n`));
  if (ticketData.transaction_id && ticketData.payment_mode === "UPI") {
    cmd.push(...enc(`Txn ID   : ${ticketData.transaction_id}\n`));
  }
  cmd.push(...enc(line(chars)));

  // ── Footer ────────────────────────────────────────────────────────────────
  cmd.push(27, 97, 1); // Center
  cmd.push(...enc("Have Fun! Stay Safe!\n"));
  cmd.push(...enc("Please retain this ticket\n"));

  // Feed + cut
  cmd.push(10, 10, 10, 10);
  cmd.push(29, 86, 1);

  return new Uint8Array(cmd);
};

// ─── Public: Print Ticket ─────────────────────────────────────────────────────
export const printFunZoneTicket = (ticketData) => {
  if (!cachedPrinterConfig) {
    console.error(
      "❌ Printer not configured. Call loadFunZonePrinterSettings first.",
    );
    alert("Printer not configured. Please check FunZone Settings.");
    return;
  }

  const formatted = formatTicket(ticketData);
  const contentString = new TextDecoder().decode(formatted);
  sendToPrinterHelper(contentString);
};
